import { reapplyThemeRespectingA11y } from 'utils/theme-preference';

export const A11Y_TEXT_SCALE = 'a11y-text-scale';
export const A11Y_TEXT_SPACING = 'a11y-text-spacing';
export const A11Y_HIGH_CONTRAST = 'a11y-high-contrast';
export const A11Y_UNDERLINE_LINKS = 'a11y-underline-links';
export const A11Y_REDUCE_MOTION = 'a11y-reduce-motion';
export const A11Y_FOCUS_STRONG = 'a11y-focus-strong';

/** localStorage key for pre-login accessibility mode (login / public pages). */
export const A11Y_LOGIN_MODE_STORAGE_KEY = 'a11y-login-mode-enabled';

/** Applied on `html` when login accessibility mode is active (shows form labels, etc.). */
export const A11Y_LOGIN_MODE_CLASS = 'a11y-login-mode';

/** Preset used on the login screen when accessibility mode is enabled. */
export const LOGIN_A11Y_PRESET: A11yPreferences = {
  textScale: 1,
  textSpacing: 'relaxed',
  highContrast: true,
  underlineLinks: true,
  reduceMotion: 'off',
  focusStrong: true,
};

export type A11yTextScale = 1 | 1.125 | 1.25;
export type A11yTextSpacing = 'default' | 'relaxed';
export type A11yReduceMotion = 'system' | 'on' | 'off';

export type A11yPreferences = {
  textScale: A11yTextScale;
  textSpacing: A11yTextSpacing;
  highContrast: boolean;
  underlineLinks: boolean;
  reduceMotion: A11yReduceMotion;
  focusStrong: boolean;
};

export const DEFAULT_A11Y_PREFERENCES: A11yPreferences = {
  textScale: 1,
  textSpacing: 'default',
  highContrast: false,
  underlineLinks: false,
  reduceMotion: 'off',
  focusStrong: false,
};

const A11Y_CLASS_NAMES = [
  'a11y-text-large',
  'a11y-text-larger',
  'a11y-text-spacing-relaxed',
  'a11y-high-contrast',
  'a11y-underline-links',
  'a11y-reduce-motion',
  'a11y-focus-strong',
] as const;

const toTextScale = (value: unknown): A11yTextScale => {
  const n = Number(value);
  if (n >= 1.25) return 1.25;
  if (n >= 1.125) return 1.125;
  return 1;
};

const toTextSpacing = (value: unknown): A11yTextSpacing =>
  value === 'relaxed' ? 'relaxed' : 'default';

const toReduceMotion = (value: unknown): A11yReduceMotion => {
  if (value === 'on' || value === 'off' || value === 'system') return value;
  return 'off';
};
const toBoolean = (value: unknown, fallback = false): boolean => {
  if (typeof value === 'boolean') return value;
  if (value === 'true') return true;
  if (value === 'false') return false;
  return fallback;
};

export const normalizeA11yPreferences = (
  partial?: Partial<Record<string, unknown>> | null
): A11yPreferences => ({
  textScale: toTextScale(partial?.[A11Y_TEXT_SCALE]),
  textSpacing: toTextSpacing(partial?.[A11Y_TEXT_SPACING]),
  highContrast: toBoolean(partial?.[A11Y_HIGH_CONTRAST]),
  underlineLinks: toBoolean(partial?.[A11Y_UNDERLINE_LINKS]),
  reduceMotion: toReduceMotion(partial?.[A11Y_REDUCE_MOTION]),
  focusStrong: toBoolean(partial?.[A11Y_FOCUS_STRONG]),
});

const shouldReduceMotion = (mode: A11yReduceMotion): boolean => {
  if (mode === 'on') return true;
  if (mode === 'off') return false;
  if (typeof window === 'undefined' || !window.matchMedia) return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
};

export const clearA11yPreferences = (): void => {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;
  A11Y_CLASS_NAMES.forEach((className) => html.classList.remove(className));
  html.classList.remove(A11Y_LOGIN_MODE_CLASS);
  html.style.removeProperty('--a11y-font-scale');
};

export const isLoginA11yModeEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  return window.localStorage.getItem(A11Y_LOGIN_MODE_STORAGE_KEY) === 'true';
};

export const setLoginA11yModeEnabled = (enabled: boolean): void => {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(A11Y_LOGIN_MODE_STORAGE_KEY, enabled ? 'true' : 'false');
};

/** Toggle login-page accessibility mode (persisted in localStorage for public pages). */
export const applyLoginA11yMode = (enabled: boolean): void => {
  if (typeof document === 'undefined') return;
  setLoginA11yModeEnabled(enabled);
  const html = document.documentElement;
  if (enabled) {
    html.classList.add(A11Y_LOGIN_MODE_CLASS);
    applyA11yPreferences(LOGIN_A11Y_PRESET);
    return;
  }
  html.classList.remove(A11Y_LOGIN_MODE_CLASS);
  clearA11yPreferences();
  // Restore system/user theme now that a11y is no longer forcing light.
  reapplyThemeRespectingA11y();
};

export const applyA11yPreferences = (
  prefs: A11yPreferences = DEFAULT_A11Y_PREFERENCES
): void => {
  if (typeof document === 'undefined') return;
  const html = document.documentElement;

  // Ensure document language is set (client webpack shell may omit it)
  if (!html.getAttribute('lang')) {
    html.setAttribute('lang', 'en');
  }

  A11Y_CLASS_NAMES.forEach((className) => html.classList.remove(className));

  html.style.setProperty('--a11y-font-scale', String(prefs.textScale));
  if (prefs.textScale >= 1.25) {
    html.classList.add('a11y-text-larger');
  } else if (prefs.textScale >= 1.125) {
    html.classList.add('a11y-text-large');
  }

  if (prefs.textSpacing === 'relaxed') {
    html.classList.add('a11y-text-spacing-relaxed');
  }
  if (prefs.highContrast) {
    html.classList.add('a11y-high-contrast');
  }
  if (prefs.underlineLinks) {
    html.classList.add('a11y-underline-links');
  }
  if (shouldReduceMotion(prefs.reduceMotion)) {
    html.classList.add('a11y-reduce-motion');
  }
  if (prefs.focusStrong) {
    html.classList.add('a11y-focus-strong');
  }

  // High contrast / login a11y force light; turning them off restores saved theme.
  reapplyThemeRespectingA11y();
};

export const syncLoginA11yModeFromStorage = (): void => {
  applyLoginA11yMode(isLoginA11yModeEnabled());
};

export const A11Y_PREFERENCE_KEYS = [
  A11Y_TEXT_SCALE,
  A11Y_TEXT_SPACING,
  A11Y_HIGH_CONTRAST,
  A11Y_UNDERLINE_LINKS,
  A11Y_REDUCE_MOTION,
  A11Y_FOCUS_STRONG,
] as const;

export const a11yPreferencesToPreferenceRecords = (
  prefs: A11yPreferences
): Array<{ name: string; value: unknown }> => [
  { name: A11Y_TEXT_SCALE, value: prefs.textScale },
  { name: A11Y_TEXT_SPACING, value: prefs.textSpacing },
  { name: A11Y_HIGH_CONTRAST, value: prefs.highContrast },
  { name: A11Y_UNDERLINE_LINKS, value: prefs.underlineLinks },
  { name: A11Y_REDUCE_MOTION, value: prefs.reduceMotion },
  { name: A11Y_FOCUS_STRONG, value: prefs.focusStrong },
];

/** If login accessibility mode was enabled on a public page, return its preset once. */
export const consumeLoginA11yModePreset = (): A11yPreferences | null => {
  if (!isLoginA11yModeEnabled()) {
    return null;
  }
  setLoginA11yModeEnabled(false);
  return LOGIN_A11Y_PRESET;
};
